/**
 * ICE Server Prefetch
 *
 * Pre-fetches TURN credentials -- before React mounts -- so the peer build that
 * follows starts with credentials already in hand. When credentials arrive it
 * pre-warms an RTCPeerConnection with iceCandidatePoolSize so STUN/TURN binding
 * starts as early as possible.
 *
 * This is a load-time session starter (it opens a network fetch and, on success,
 * a live RTCPeerConnection), so it must not run on a speculative load: a Safari
 * top-hit preload or a Chromium speculation-rules pre-render executes this module
 * without a real user present. Both the fetch and the pre-warm are therefore
 * gated behind `whenHumanPageLoad()`. Callers that need the value await the same
 * gated promise (`getPreFetchedIceServers`), so a caller that runs after the gate
 * has settled sees the prefetch resolve normally.
 */
import constants from '@playcastdotio/GlobalConfig';
import { sendAnalytics } from '../../telemetry/client';
import { whenHumanPageLoad } from '../../utils/prerenderGuard';

export type IceServerCredentials = {
  urls: string[];
  username: string;
  credential: string;
};

let cachedCredentials: IceServerCredentials | null = null;

function getQuickstartSlugFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const match = window.location.pathname.match(/^\/q\/([^/]+)/);
  return match?.[1] || undefined;
}

function getBeaconShareCodeFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const match = window.location.pathname.match(/^\/j\/([^/]+)/);
  return match?.[1] || undefined;
}

const quickstartSlug = getQuickstartSlugFromUrl();
const isQuickstartPage = quickstartSlug !== undefined;

const beaconShareCode = getBeaconShareCodeFromUrl();
const isBeaconPage = beaconShareCode !== undefined;

if (isQuickstartPage) {
  whenHumanPageLoad().then(() => {
    sendAnalytics({
      name: 'ICE Server Prefetch Started',
      role: 'player',
      flow: 'quickstart',
      quickstartSlug,
    });
  });
}

if (isBeaconPage) {
  whenHumanPageLoad().then(() => {
    sendAnalytics({
      name: 'ICE Server Prefetch Started',
      role: 'player',
      flow: 'beacon',
      shareCode: beaconShareCode,
    });
  });
}

const iceServerPromise: Promise<IceServerCredentials | null> = whenHumanPageLoad()
  .then(() =>
    fetch(`${constants.REALTIME_API_BASE_URL}realtime/ice-servers`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data?.iceServers ?? null)
      .then((creds) => {
        cachedCredentials = creds;
        return creds;
      }),
  )
  .catch(() => null);

if (isQuickstartPage) {
  iceServerPromise.then((creds) => {
    sendAnalytics({
      name: 'ICE Server Prefetch Completed',
      role: 'player',
      flow: 'quickstart',
      quickstartSlug,
      success: creds !== null,
    });
  });
}

if (isBeaconPage) {
  iceServerPromise.then((creds) => {
    sendAnalytics({
      name: 'ICE Server Prefetch Completed',
      role: 'player',
      flow: 'beacon',
      shareCode: beaconShareCode,
      success: creds !== null,
    });
  });
}

// Pre-warm the RTCPeerConnection the instant credentials arrive.
// Import is lazy to avoid circular dependency (peer.ts imports us).
iceServerPromise.then((creds) => {
  if (!creds) return;
  import('./peer').then(({ preWarmPeerConnection }) =>
    preWarmPeerConnection(creds).catch((err) =>
      console.warn('[iceServerPrefetch] Pre-warm failed:', err),
    ),
  );
});

export function getPreFetchedIceServers(): Promise<IceServerCredentials | null> {
  return iceServerPromise;
}

/**
 * Synchronously check if ICE servers have already been fetched.
 * Returns true if the prefetch resolved successfully.
 */
export function hasPreFetchedIceServers(): boolean {
  return cachedCredentials !== null;
}
